import { IUINotification } from '@/src/types/interfaces';
import { ENotificationType } from 'types/src/types';

type TextStyle =
  | { type: 'plain'; value: string; isRed?: boolean }
  | { type: 'bold'; value: string; isRed?: boolean };

const notificationMessages: Record<
  ENotificationType,
  (notification: IUINotification) => TextStyle[]
> = {
  [ENotificationType.NEW_BIT]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    { type: 'plain', value: ' just posted a new Bit.' },
  ],
  [ENotificationType.APPROVED_PROJECT]: (notification) => [
    { type: 'bold', value: notification.slug ?? 'Your project' },
    { type: 'plain', value: ' just published.' },
  ],
  [ENotificationType.REJECTED_PROJECT]: (notification) => [
    { type: 'bold', value: 'Your project was rejected.', isRed: true },
    { type: 'plain', value: notification.reason as string },
  ],
  [ENotificationType.ERROR_PROJECT]: (notification) => [
    { type: 'bold', value: 'Error occurred while publishing', isRed: true },
    { type: 'plain', value: 'Please, try again later.' },
  ],
  [ENotificationType.COMMENT_BIT]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    { type: 'plain', value: ' commented on your Bit.' },
  ],
  [ENotificationType.LIKE_BIT]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    { type: 'plain', value: ' liked your Bit.' },
  ],
  [ENotificationType.REVIEW_PROJECT]: (notification) => [
    { type: 'plain', value: 'Your project was reviewed ' },
    { type: 'bold', value: `by ${notification.producer?.name ?? 'Someone'}` },
  ],
  [ENotificationType.FOLLOW]: () => [
    { type: 'plain', value: 'You’ve got a new follower.' },
  ],
  [ENotificationType.SUBSCRIPTION]: () => [
    { type: 'plain', value: 'You have a new subscriber!' },
  ],
  [ENotificationType.DONATION]: (notification) => [
    { type: 'plain', value: 'You received a ' },
    {
      type: 'bold',
      value: `$${Number(notification.donationAmount).toFixed(2)}`,
    },
    { type: 'plain', value: ' donation!' },
  ],
  [ENotificationType.PAYOUT]: (notification) => [
    { type: 'plain', value: 'Your monthly payout of ' },
    {
      type: 'bold',
      value: `$${Number(notification.earningsAmount).toFixed(2)}`,
    },
    { type: 'plain', value: ' is on the way!' },
  ],
  [ENotificationType.SUPERLIKE_RECEIVED]: (notification) => [
    { type: 'bold', value: `Your comment: ${notification.reason}` },
    {
      type: 'plain',
      value: ` was superliked by ${notification.producer?.name ?? 'Someone'}`,
    },
  ],
  [ENotificationType.SMBD_REPLIED_YOUR_COMMENT]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    {
      type: 'plain',
      value: ` replied to your comment: ${notification.reason}`,
    },
  ],
  [ENotificationType.NEW_COMMENT_ON_YOUR_POST]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    { type: 'plain', value: ` commented on your post: ${notification.reason}` },
  ],
  [ENotificationType.MENTIONED_IN_COMMENT]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    {
      type: 'plain',
      value: ` mentioned you in a comment: ${notification.reason}`,
    },
  ],
  [ENotificationType.MENTIONED_IN_POST]: (notification) => [
    { type: 'bold', value: notification.producer?.name ?? 'Someone' },
    {
      type: 'plain',
      value: ` mentioned you in a post: ${notification.reason}`,
    },
  ],
};

const notificationLinks: Record<
  ENotificationType,
  (notification: IUINotification) => string
> = {
  [ENotificationType.NEW_BIT]: (notification) => `/bit/${notification.slug}`,
  [ENotificationType.APPROVED_PROJECT]: (notification) =>
    `/project/${notification.slug}`,
  [ENotificationType.REJECTED_PROJECT]: (notification) =>
    `/project/${notification.slug}`,
  [ENotificationType.ERROR_PROJECT]: (notification) =>
    `/project/${notification.slug}`,
  [ENotificationType.REVIEW_PROJECT]: (notification) =>
    `/project/${notification.slug}/reviews`,
  [ENotificationType.COMMENT_BIT]: (notification) =>
    `/bit/${notification.slug}`,
  [ENotificationType.LIKE_BIT]: (notification) => `/bit/${notification.slug}`,
  [ENotificationType.FOLLOW]: (notification) =>
    `/${notification.producer?.uniqueLink || ''}`,
  [ENotificationType.SUBSCRIPTION]: (notification) =>
    `/${notification.producer?.uniqueLink || ''}`,
  [ENotificationType.DONATION]: () => '/finances?tab=donations',
  [ENotificationType.PAYOUT]: () => '/finances?tab=earnings',
  [ENotificationType.SUPERLIKE_RECEIVED]: (notification) =>
    `${notification.slug}`,
  [ENotificationType.SMBD_REPLIED_YOUR_COMMENT]: (notification) =>
    `${notification.slug}`,
  [ENotificationType.NEW_COMMENT_ON_YOUR_POST]: (notification) =>
    `${notification.slug}`,
  [ENotificationType.MENTIONED_IN_COMMENT]: (notification) =>
    `${notification.slug}`,
  [ENotificationType.MENTIONED_IN_POST]: (notification) =>
    `${notification.slug}`,
};

export function getNotificationData(notification: IUINotification): {
  text: TextStyle[];
  link: string;
} {
  const text = notificationMessages[notification.type](notification);
  const link = notificationLinks[notification.type](notification);
  return { text, link };
}
